import { useEffect, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import { useWorkspace } from '../context';
import { ShareButton } from '../sharing';
import { SheetNote } from '../share-sheet/share-sheet';
import { WorkspaceIcon } from '../icons';
import { useIntlLocale } from '../intl-locale';
import { projectsApi } from './api';
import { dropProject, projectScope } from './use-projects';
import { CompanyRequestError } from '@/company/api';
import { firstName } from './labels';
import type { ProjectDetail, ProjectPerson, ProjectReadFirst } from './types';

/** The project's own Share: the one Share sheet, with Can use and Can edit. */
export function ProjectShareButton({
  project,
  onChange,
  label,
}: {
  project: ProjectDetail;
  onChange: (next: ProjectDetail) => void;
  /** The trigger's own words, as an empty project's "Share with the people doing it". */
  label?: string;
}) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const sharer = project.role !== 'viewer';
  return (
    <ShareButton
      kind="project"
      label={label}
      item={{
        id: project.id,
        title: project.name,
        visibility: project.visibility,
        audience: project.audience,
        sharingRevision: project.sharingRevision,
        canShare: sharer,
        canEdit: sharer,
      }}
      audienceReasons={
        project.role === 'owner'
          ? {}
          : { private: formatCopy(copy.onlyOwnerPrivate, { name: project.owner.name }) }
      }
      onSaved={() => {
        const inScope = projectScope();
        void projectsApi
          .get(project.id)
          .then((next) => {
            if (inScope()) onChange(next);
          })
          .catch((failure: unknown) => {
            if (!inScope()) return;
            if (failure instanceof CompanyRequestError && failure.status === 404) {
              // Shared so that this person no longer reaches it: leave for the list.
              dropProject(project.id);
              ws.notify(formatCopy(copy.sharingSaved, { name: project.name }), null, 'info');
              ws.openProject(null);
              return;
            }
            ws.notify(formatCopy(copy.sharingSavedUnread, { name: project.name }), null, 'error');
          });
      }}
    />
  );
}

function names(people: ProjectPerson[], locale: string) {
  const first = people.map((person) => firstName(person.name));
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(first);
}

/**
 * Rule 7: everyone in the project should be able to open what every chat reads
 * first. When people are being added, say who can't, so the person sharing
 * decides; nothing is shared for them.
 */
export function ProjectReadsFirstCheck({
  projectId,
  userIds,
  company = false,
}: {
  projectId: string;
  userIds: string[];
  company?: boolean;
}) {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const [gaps, setGaps] = useState<(ProjectReadFirst & { cannotOpen: ProjectPerson[] })[]>([]);
  const [failed, setFailed] = useState(false);
  const key = userIds.join(',');
  useEffect(() => {
    setGaps([]);
    setFailed(false);
    if (!company && !key) {
      return;
    }
    let live = true;
    void projectsApi
      .accessCheck(projectId, company ? { company: true } : { userIds: key.split(',') })
      .then((result) => {
        if (live) setGaps(result.filter((item) => item.cannotOpen.length > 0));
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [projectId, key, company]);
  if (failed) return <SheetNote>{copy.sharingCheckFailed}</SheetNote>;
  if (!gaps.length) return null;
  return (
    <>
      {gaps.map((item) => (
        <SheetNote key={`${item.kind}:${item.id}`}>
          <strong>
            {formatCopy(copy.cannotOpenShare, {
              people: names(item.cannotOpen, locale),
              title: item.title,
            })}
          </strong>{' '}
          {copy.readsFirstShare}{' '}
          <button
            type="button"
            className="ws-text-button"
            onClick={() =>
              ws.openCanvas(
                item.kind === 'file'
                  ? { kind: 'file', id: item.id }
                  : { kind: 'page', id: item.id },
              )
            }
          >
            {copy.openToShare} <WorkspaceIcon name="arrow" size={13} />
          </button>
        </SheetNote>
      ))}
    </>
  );
}
